import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import "./CustomToast.css";
import { TriangleAlert } from 'lucide-react';
import { CircleCheckBig } from 'lucide-react';
import { Info } from 'lucide-react';

// Icons mapping
const ICONS = {
  success: <CircleCheckBig size={40} color="#10b981" />,
  error: <TriangleAlert size={40} color="#ef4444" />,
  info: <Info size={40} color="#3b82f6" />
};

// 1. Singleton to store the showToast function for global access
let showToastInstance = null;

export const customToast = (type, message) => {
  if (showToastInstance) {
    showToastInstance(type.toLowerCase(), message);
  } else {
    console.warn("ToastProvider not found. Wrap your app with <ToastProvider />.");
  }
};

// 2. Toast Context
const ToastContext = createContext();

// 3. Custom Hook (Optional, but still useful)
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

// 4. UI Component (Internal)
const ToastCard = ({ type, message, onClose, duration = 10000 }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [remainingTime, setRemainingTime] = useState(duration);
  
  const timerRef = React.useRef(null);
  const startTimeRef = React.useRef(Date.now());

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(onClose, 300); // Match animation duration
  }, [onClose]);

  useEffect(() => {
    if (!isPaused) {
      startTimeRef.current = Date.now();
      timerRef.current = setTimeout(handleClose, remainingTime);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isPaused, remainingTime, handleClose]);

  const handleMouseEnter = () => {
    setIsPaused(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    const elapsed = Date.now() - startTimeRef.current;
    setRemainingTime((prev) => Math.max(prev - elapsed, 0));
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  const title = type.charAt(0).toUpperCase() + type.slice(1);

  return (
    <div className={`toast-overlay ${isClosing ? "fade-out" : ""}`}>
      <div 
        className={`toast-card toast-${type} ${isClosing ? "scale-down" : ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button className="toast-close-btn" onClick={handleClose}>
          &times;
        </button>
        
        <div className={`toast-icon-wrapper toast-icon-wrapper-${type}`}>
          {ICONS[type]}
        </div>

        <h3 className="toast-title">{title}</h3>
        <p className="toast-message">{message}</p>

        <button className={`toast-action-btn toast-action-btn-${type}`} onClick={handleClose}>
          OK
        </button>

        <div className="toast-progress-container">
          <div 
            className="toast-progress-bar" 
            style={{ 
              animation: `progressAnim ${duration}ms linear forwards`,
              animationPlayState: isPaused ? 'paused' : 'running'
            }}
          />
        </div>
      </div>
    </div>
  );
};

// 5. Provider Component
export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(null);

  const showToast = useCallback((type, message) => {
    if (!message) return;
    setToast({ type, message, id: Date.now() });
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  // Set the global instance when the provider mounts
  useEffect(() => {
    showToastInstance = showToast;
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      {toast && (
        <ToastCard
          type={toast.type}
          message={toast.message}
          onClose={hideToast}
          duration={3000}
        />
      )}
    </ToastContext.Provider>
  );
};

// Export standalone function as default
export default customToast;
